import * as React from 'react';

import { Button, Grid, Link, PasswordField, TextField } from '@emburse/ui-core';

import { EmburseAccountsLeftContentContainer } from '@ReactComponents/Shared/Account/EmburseAccountsLeftContentContainer';
import { TestSiteBanner } from '@ReactComponents/Shared/TestSiteBanner/TestSiteBanner';

import { SamlEmailVerificationApi } from '@ClientServices/api/samlEmailVerificationApi';

import { AccountIconWithEmail } from './AccountIconWithEmail';
import { getUserEmailFromSessionStorage, removeUserEmailFromSessionStorage, setUserEmailToSessionStorage } from './LoginFormSessionStorage';
import { PrivacyPolicy } from './PrivacyPolicy';

interface IRedesignedLoginFormProps {
  csrf: string;
  emburseLoginUrl: string;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

interface IExtendedWindow extends Window {
  featureFlags: any;
  FEATURES: any;
}

export const RedesignedLoginForm: React.FC<IRedesignedLoginFormProps> = ({ csrf, onSubmit, emburseLoginUrl }) => {
  const [emailFieldEnabled, setEmailFieldEnabled] = React.useState(true);
  const [email, setEmail] = React.useState('');
  const [emailVerificationLoading, setEmailVerificationLoading] = React.useState(false);

  const [showPasswordField, setShowPasswordField] = React.useState(false);

  // Rendering the TestSiteBanner pushes the privacy policy down too far
  const privacyPolicyGridHeight = window.FEATURES.test_site ? '600px' : '700px';

  React.useEffect(() => {
    const userEmailFromSessionStorage = getUserEmailFromSessionStorage();
    if (userEmailFromSessionStorage) {
      setEmailFieldEnabled(false);
      setEmail(userEmailFromSessionStorage);
      setShowPasswordField(true);
    }
  }, []);

  const handleEmailChange = async (e: any) => {
    e.preventDefault();

    if (email) {
      setUserEmailToSessionStorage(email);
      setEmailFieldEnabled(false);
      setEmailVerificationLoading(true);
      try {
        const res = await SamlEmailVerificationApi.checkIfUserHasSSO(email, csrf);
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl;
          return;
        }
      } catch (error) {
        /* Do nothing */
      }
      setEmailVerificationLoading(false);
      setShowPasswordField(true);
    }
  };

  const onFormSubmit = (e: any) => {
    // If the user presses enter after typing in their email, the form will submit
    // But we instead want this to just "submit" the email, i.e. lock it in and then show the password field
    if (emailFieldEnabled && !showPasswordField) {
      e.preventDefault();
      handleEmailChange(e);
    } else {
      removeUserEmailFromSessionStorage();
      onSubmit(e);
    }
  };

  const renderEmailField = () => {
    if (emailFieldEnabled) {
      return (
        <TextField
          id="emailField"
          type="email"
          name="email"
          label="Enter email"
          autoFocus
          fullWidth
          disabled={!emailFieldEnabled}
          value={email}
          onChange={(e: any) => setEmail(e.target.value)}
          required
          data-testid="emailField"
        />
      );
    }

    return (
      <>
        <input type="hidden" name="email" value={email} />
        <Grid container direction={'row'} justifyContent={'space-between'}>
          <Grid item direction={'row'} alignItems={'center'}>
            <AccountIconWithEmail className="jc-fe" email={email} />
          </Grid>
          <Grid item>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setEmailFieldEnabled(true);
                setShowPasswordField(false);
              }}
              data-testid="editEmailButton"
            >
              Edit
            </Button>
          </Grid>
        </Grid>
      </>
    );
  };

  const renderLogInButton = () => {
    if (emailFieldEnabled) {
      return (
        <Button size="medium" fullWidth onClick={handleEmailChange} data-testid="continueButton">
          Continue
        </Button>
      );
    }

    return (
      <Button id="loginButton" className="login" type="submit" size="medium" fullWidth isLoading={emailVerificationLoading}>
        Log In
      </Button>
    );
  };

  function renderDowntime() {
    return <div>{(window as any)?.whitelabelConfiguration?.tags?.brandName} is currently unavailable due to scheduled maintenance.</div>;
  }

  function renderEmburseLoginRedirect() {
    return (
      <div>
        Redirecting to {(window as any)?.whitelabelConfiguration?.tags?.brandName} login...
        <br />
        If you are not redirected, <a href={emburseLoginUrl}>click here</a>.
      </div>
    );
  }

  function renderLoginForm() {
    if ((window as unknown as IExtendedWindow)?.FEATURES?.planned_database_downtime) {
      return renderDowntime();
    }

    if (emburseLoginUrl) {
      return renderEmburseLoginRedirect();
    }

    return (
      <form method="POST" action="/login" id="login" autoComplete="off" onSubmit={onFormSubmit}>
        <input type="hidden" name="_csrf" value={csrf} />
        <Grid container direction={'column'} spacing={2} style={{ width: '400px' }}>
          <Grid item>{renderEmailField()}</Grid>
          {showPasswordField && (
            <Grid item>
              <PasswordField id="passwordField" name="password" label="Enter Password" autoComplete="off" fullWidth autoFocus required />
            </Grid>
          )}
          <Grid item>{renderLogInButton()}</Grid>
          {showPasswordField && (
            <Grid item>
              <div className="forgotContainer">
                <Link href="/reset_password">Forgot Password?</Link>
              </div>
            </Grid>
          )}
        </Grid>
      </form>
    );
  }

  const title = (window as unknown as IExtendedWindow)?.FEATURES?.planned_database_downtime ? '' : 'Sign In';

  return (
    <EmburseAccountsLeftContentContainer title={title}>
      <Grid item>
        <TestSiteBanner />
      </Grid>
      <Grid item>{renderLoginForm()}</Grid>

      <Grid item style={{ height: privacyPolicyGridHeight, alignContent: 'flex-end', width: '450px' }}>
        <PrivacyPolicy />
      </Grid>
    </EmburseAccountsLeftContentContainer>
  );
};
